/**
 * Shared costume prompt builder.
 *
 * Same prompt is used by:
 *   • Manual "Generate Costume" button in Cast & World (CharacterSelection.jsx)
 *   • Pre-storyboard pipeline auto-fill when the user clicks
 *     "Generate Storyboard" or "Generate Video" (preStoryboardPipeline.js)
 *
 * The prompt MUST end with the explicit "no person" instruction or the LLM
 * defaults to rendering the character WEARING the costume instead of a clean
 * flat-lay of just the outfit + accessories.
 *
 * Extracted to a single source of truth so the manual and automated flows
 * can never drift apart. See user report (Feb 2026) where the pipeline's
 * short prompt was inserting a person, while the manual flow produced the
 * desired flat-lay.
 */
export const buildCostumePrompt = (charName, charDescription, script) => {
    const scriptSnippet = script && script.length > 800
        ? script.substring(0, 800) + '...'
        : (script || '');
    const name = charName || 'the character';
    const desc = charDescription || '';

    return [
        `You are a professional costume designer for a film or advertisement production.`,
        `Analyze the following story/script and character, then design a complete wardrobe look.\n`,
        `CHARACTER: "${name}"`,
        desc ? `DESCRIPTION: ${desc}` : '',
        scriptSnippet ? `\nSCRIPT/STORY:\n${scriptSnippet}\n` : '',
        `Based on the story's genre, setting, time period, mood, and this character's role and personality, design a wardrobe that:`,
        `- Reflects the character's social status, occupation, and emotional arc`,
        `- Is appropriate for the era, culture, and location described in the story`,
        `- Uses fabrics, textures, colors, and patterns that convey the character's personality`,
        `- Includes footwear, jewelry, watches, bags, hats, scarves, or any accessories that complete the look`,
        `- Adds any story-relevant props (e.g., a detective's magnifying glass, a chef's apron, a soldier's dog tags)\n`,
        `OUTPUT: A single photorealistic image of the complete outfit and all accessories laid out flat on a clean neutral background (light grey or white).`,
        `Style: Professional fashion flat-lay photography, editorial quality, sharp detail, soft even lighting.`,
        `IMPORTANT: Show ONLY the clothing, shoes, and accessories — absolutely no human body, mannequin, or person wearing them.`,
    ].filter(Boolean).join('\n');
};

export default buildCostumePrompt;
